import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../services/api';

interface AdminContextType {
  isAdmin: boolean;
  isLoading: boolean;
  login: (pin: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AdminContext = createContext<AdminContextType>({
  isAdmin: false,
  isLoading: true,
  login: async () => ({ success: false }),
  logout: () => {},
});

export const useAdmin = () => useContext(AdminContext);

const TOKEN_KEY = 'ak_admin_token';
const EXPIRES_KEY = 'ak_admin_expires';

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const clearAuth = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_KEY);
    setIsAdmin(false);
  }, []);

  const setAuthHeader = useCallback((token: string) => {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, []);

  const removeAuthHeader = useCallback(() => {
    delete apiClient.defaults.headers.common['Authorization'];
  }, []);

  // Check existing token on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      const expires = localStorage.getItem(EXPIRES_KEY);

      if (!token || !expires) {
        setIsLoading(false);
        return;
      }

      // Quick client-side expiry check
      if (new Date(expires) < new Date()) {
        clearAuth();
        removeAuthHeader();
        setIsLoading(false);
        return;
      }

      // Verify with backend
      try {
        const response = await apiClient.post('/admin/verify', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.valid) {
          setIsAdmin(true);
          setAuthHeader(token);
        } else {
          clearAuth();
          removeAuthHeader();
        }
      } catch {
        clearAuth();
        removeAuthHeader();
      }

      setIsLoading(false);
    };

    checkAuth();
  }, [clearAuth, setAuthHeader, removeAuthHeader]);

  const login = async (pin: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await apiClient.post('/admin/login', { pin });
      const { token, expiresAt } = response.data;
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(EXPIRES_KEY, expiresAt);
      setAuthHeader(token);
      setIsAdmin(true);
      return { success: true };
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Login failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    clearAuth();
    removeAuthHeader();
  };

  return (
    <AdminContext.Provider value={{ isAdmin, isLoading, login, logout }}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
